import React, { RefObject, useRef, useEffect } from 'react';
import { ChatMessage } from './ChatMessage';
import { ChatInput, ChatInputRef } from './ChatInput';
import { DocumentBrowser } from './DocumentBrowser';
import { TypingIndicator } from './TypingIndicator';
import { Message } from '../AiChatSlideout';
import styles from './ChatView.module.scss';

interface ChatViewProps {
  messages: Message[];
  input: string;
  isLoading: boolean;
  selectedDocumentName: string | null;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onDocumentSelect: (documentId: string, documentName: string) => void;
  onBack?: () => void;
  scrollRef: RefObject<HTMLDivElement>;
  currentDealId?: string;
}

export const ChatView: React.FC<ChatViewProps> = ({
  messages,
  input,
  isLoading,
  selectedDocumentName,
  onInputChange,
  onSendMessage,
  onDocumentSelect,
  onBack: _onBack,
  scrollRef,
  currentDealId,
}) => {
  const chatInputRef = useRef<ChatInputRef>(null);

  // Auto-focus input when a document is selected
  useEffect(() => {
    if (selectedDocumentName && !isLoading) {
      // Small delay to ensure the input is rendered and enabled
      const timer = setTimeout(() => {
        chatInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedDocumentName, isLoading]);

  // Wrapper to handle document selection and auto-focus
  const handleDocumentSelect = (documentId: string, documentName: string) => {
    onDocumentSelect(documentId, documentName);
    // Focus will be handled by the useEffect above when selectedDocumentName changes
  };

  return (
    <div className={styles.chatView}>
      {/* Document Context Bar */}
      {selectedDocumentName && (
        <div className={styles.documentContext}>
          <span className={styles.documentLabel}>Chatting about:</span>
          <span className={styles.documentName}>{selectedDocumentName}</span>
        </div>
      )}

      {/* Messages Area */}
      <div className={styles.messagesArea} ref={scrollRef}>
        {messages.length === 0 && !selectedDocumentName ? (
          <div className={styles.documentBrowserWrapper}>
            <DocumentBrowser
              onDocumentSelect={handleDocumentSelect}
              currentDealId={currentDealId}
            />
          </div>
        ) : (
          <div className={styles.messagesList}>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            {isLoading && <TypingIndicator />}
          </div>
        )}
      </div>

      {/* Input Area */}
      <ChatInput
        ref={chatInputRef}
        value={input}
        onChange={onInputChange}
        onSend={onSendMessage}
        disabled={isLoading || !selectedDocumentName}
        placeholder={
          selectedDocumentName 
            ? `Ask about ${selectedDocumentName}...` 
            : 'Select a document first'
        }
      />
    </div>
  );
};